import * as m from '@staiter/i18n';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './Toast.module.css';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export type ToastItem = {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /**
   * S-FIX-5-FE.9.1: trace_id показывается отдельной строкой (monospace, мелким),
   * не клипуется truncate'ом description. Click-to-copy для поддержки.
   */
  traceId?: string;
  durationMs?: number;
  /** notification-center F6 — deep-link по клику на тело тоста. */
  href?: string;
  /**
   * Пак context-menu (F9) — обратимое действие в тосте (Undo).
   *
   * С-I2 Р.8, круг 1 гейта (UI-2, MAJOR): действие может вернуть обещание успеха — тогда тост
   * закрывается только при `true`. Копирование одноразовой ссылки закрывало тост до ответа буфера
   * обмена, и при отказе ссылка терялась.
   */
  action?: ToastAction;
  /**
   * С-I2 Р.8 (TCA-B10, В-50) — второе действие рядом с основным (тост приглашения: «Скопировать ссылку»
   * и «К приглашениям»). Как и основное, закрывает тост.
   */
  secondaryAction?: ToastAction;
  /**
   * С-I2 Р.8, круг 1 (UX-2, UI-2): значение, которое должно быть видно и выделяться целиком, —
   * ссылка приглашения. Поле только для чтения: видно, что копируешь, и есть путь в обход буфера.
   */
  field?: { value: string; label: string };
};

export type ToastAction = {
  label: string;
  // biome-ignore lint/suspicious/noConfusingVoidType: обычные действия («Вернуть») ничего не возвращают
  onClick: () => void | Promise<boolean>;
  icon?: ReactNode;
  /**
   * С-I2 Р.8, круг 2 гейта (UX-1, MAJOR): действие не закрывает тост. «К приглашениям» закрывало тост
   * и молча уничтожало нескопированную одноразовую ссылку.
   */
  keepOpen?: boolean;
};

type ToastContextValue = {
  push: (toast: Omit<ToastItem, 'id'> & { id?: string }) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 5000;

/**
 * E-FE.11 — defensive truncate. Гарантирует, что даже при шальной длинной строке
 * (раскрытый stack-trace, JSON-дамп) toast не ломает layout. Под лимитом текст
 * не трогается, поэтому ICU-параметры и обычные локализованные сообщения не страдают.
 */
function truncateToastText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1)}…`;
}

import { subscribeToast, toastNavigate } from './toast-bus';

/**
 * F108 пака `tails-closeout` (С-I) — тосты переживают перемонтирование провайдера.
 *
 * Провайдер живёт внутри поддерева, которое перемонтируется ключом при смене компании (эпоха
 * охвата у `QueryClientProvider`) и языка (`RuntimeLocaleProvider`). Тост, выпущенный в момент
 * смены, — «Вы в «ТОО Альфа» · Вернуться» с экрана отказа — пропадал вместе со старым деревом.
 * Новый провайдер забирает живые тосты при первой отрисовке: она идёт ДО очистки старого
 * дерева, а очистка обнуляет запас, чтобы тосты не перетекали между независимыми провайдерами.
 */
let survivingToasts: ToastItem[] = [];

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>(() => survivingToasts);
  useEffect(() => {
    survivingToasts = items;
  }, [items]);
  useEffect(
    () => () => {
      survivingToasts = [];
    },
    [],
  );
  const counterRef = useRef(0);
  // F6 — навигация тоста делается через history+popstate в toast-bus (ToastProvider
  // монтируется ВНЕ RouterProvider, поэтому useRouter здесь недоступен).

  const dismiss = useCallback((id: string): void => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push: ToastContextValue['push'] = useCallback((toast) => {
    counterRef.current += 1;
    const id = toast.id ?? `toast-${Date.now()}-${counterRef.current}`;
    setItems((prev) => [...prev, { id, ...toast }]);
    return id;
  }, []);

  // F5 — подписка на toast-bus: QueryClient.onError / MutationCache.onError
  // push-ят через bus, React ToastProvider рендерит.
  useEffect(() => {
    return subscribeToast((t) => {
      push({ ...t });
    });
  }, [push]);

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className={styles.region}
        role="region"
        aria-label={m.toast_region_label()}
        aria-live="polite"
        /* F25 — маркер плавающего слоя. Область тостов лежит в `body`, то есть
           формально «вне» любой панели: без маркера клик по «Вернуть» считался бы
           кликом мимо и закрывал карточку задачи вместе с откатом. */
        data-toast-root=""
      >
        {items.map((t) => (
          <ToastView key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
}

/**
 * С-I2 Р.8, круг 4 гейта (UI-4): адрес в тексте тоста держится неразрывным дефисом U+2011, чтобы не
 * рваться по строкам. Скопированный адрес с U+2011 почтовый клиент не примет — при копировании
 * возвращаем обычный дефис.
 */
function copyPlainHyphens(e: React.ClipboardEvent<HTMLElement>): void {
  const text = window.getSelection()?.toString() ?? '';
  if (!text.includes('\u2011')) return;
  e.clipboardData.setData('text/plain', text.replace(/\u2011/g, '-'));
  e.preventDefault();
}

const ICON: Record<ToastVariant, ReactNode> = {
  info: <Info size={16} aria-hidden="true" />,
  success: <CheckCircle2 size={16} aria-hidden="true" />,
  warning: <AlertTriangle size={16} aria-hidden="true" />,
  error: <XCircle size={16} aria-hidden="true" />,
};

function ToastView({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}): JSX.Element {
  const run = (action: ToastAction | undefined): void => {
    if (action?.keepOpen) {
      action.onClick();
      return;
    }
    const result = action?.onClick();
    if (result instanceof Promise) {
      void result.then((ok) => {
        if (ok) onDismiss(toast.id);
      });
    } else {
      onDismiss(toast.id);
    }
  };
  useEffect(() => {
    const duration = toast.durationMs ?? DEFAULT_DURATION_MS;
    if (duration <= 0) return;
    const timer = setTimeout(() => onDismiss(toast.id), duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.durationMs, onDismiss]);

  return (
    <div
      className={styles.toast}
      data-variant={toast.variant}
      role={toast.variant === 'error' ? 'alert' : 'status'}
    >
      <span className={styles.icon}>{ICON[toast.variant]}</span>
      <div className={styles.body}>
        {toast.href ? (
          // F6 — deep-link: клик по заголовку ведёт на карточку задачи.
          <button
            type="button"
            className={[styles.title, styles.titleLink].join(' ')}
            onClick={() => {
              if (toast.href) toastNavigate(toast.href);
              onDismiss(toast.id);
            }}
          >
            {truncateToastText(toast.title, 120)}
          </button>
        ) : (
          <div className={styles.title}>{truncateToastText(toast.title, 120)}</div>
        )}
        {toast.description && (
          <div className={styles.description} onCopy={copyPlainHyphens}>
            {truncateToastText(toast.description, 300)}
          </div>
        )}
        {toast.traceId && (
          <button
            type="button"
            className={styles.trace}
            title={m.toast_copy_trace({ trace: toast.traceId })}
            aria-label={m.toast_copy_trace({ trace: toast.traceId })}
            onClick={() => {
              // S-FIX-9-FE §6: copy full UUID к clipboard.
              if (toast.traceId) navigator.clipboard?.writeText(toast.traceId).catch(() => {});
            }}
          >
            {/* S-FIX-9-FE §6: visible label = first 8 chars (short), tooltip + clipboard = full UUID. */}
            Trace: {toast.traceId.slice(0, 8)}
          </button>
        )}
        {toast.field && (
          <input
            type="text"
            readOnly
            className={styles.field}
            value={toast.field.value}
            aria-label={toast.field.label}
            onFocus={(e) => e.currentTarget.select()}
          />
        )}
        {/* С-I2 Р.8: два действия — рядом под текстом, иначе текст сжимался в узкую колонку. */}
        {toast.action && toast.secondaryAction && (
          <div className={styles.actionsRow}>
            <button type="button" className={styles.action} onClick={() => run(toast.action)}>
              {toast.action.icon}
              {toast.action.label}
            </button>
            {/* Круг 1 гейта Р.8 (UX): второе действие тише основного — тост существует ради первого. */}
            <button
              type="button"
              className={`${styles.action} ${styles.actionQuiet}`}
              onClick={() => run(toast.secondaryAction)}
            >
              {toast.secondaryAction.icon}
              {toast.secondaryAction.label}
            </button>
          </div>
        )}
      </div>
      {toast.action && !toast.secondaryAction && (
        <button type="button" className={styles.action} onClick={() => run(toast.action)}>
          {toast.action.icon}
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className={styles.close}
        onClick={() => onDismiss(toast.id)}
        aria-label={m.toast_close_notification()}
      >
        <X size={14} />
      </button>
    </div>
  );
}
